import React from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import ButtonBase from '@mui/material/ButtonBase'
import Button from 'components/Button'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { RootActionType, RootState } from 'duck'
import { ReduxProps } from 'utils/Types'
import { RenderItemProps } from 'components/ListVirtualized'
import FixedSizeList from 'components/ListVirtualized/FixedSizeList'
import GridImage from './GridImage'
import { actions, selectors, STEP } from 'containers/NFTSellPanel/duck'
import { UserImage } from 'models/ApiModels'
import { useBreakpoint } from 'utils/Hooks'
import Typography from 'components/Typography'
import { SelectUtils } from 'utils/DataProcessingUtils'
import { values } from 'appConstants'
import { GRID_PROJECTS_CONFIG } from '.'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from '../../NFTSellForm.module.scss'

const mapStateToProps = (state: RootState) => ({
  gridImages: selectors.gridImages(state),
  gridLoading: selectors.gridLoading(state),
  gridCount: selectors.gridCount(state),
  selectedGridCount: selectors.selectedGridCount(state),
  isSelectedImageEnough: selectors.isSelectedImageEnough(state),
  imageSelectionData: selectors.imageSelectionData(state),
  selectableImagesCount: selectors.selectableImagesCount(state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      toggleSelect: actions.imageSelect.toggleSelect,
      deselectAll: actions.imageSelect.deselectAll,
      selectAll: actions.imageSelect.selectAll,
      setStep: actions.setStep,
      cancel: actions.imageSelect.cancel,
      apply: actions.imageSelect.apply
    },
    dispatch
  )

type ImageSelectionProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps>

const ImageSelection: React.FC<ImageSelectionProps> = props => {
  const {
    toggleSelect,
    deselectAll,
    selectAll,
    setStep,
    apply,
    cancel,
    isSelectedImageEnough,
    gridLoading,
    gridImages,
    gridCount,
    selectedGridCount,
    imageSelectionData,
    selectableImagesCount
  } = props

  const { isSmallscreenAbove } = useBreakpoint()

  return (
    <>
      <div className={clsx('w-full', styles.ContainerImagesList)}>
        <div className={clsx('flex justify-between', stylesGlobal.BorderBottomOnSurface)}>
          <div>
            <Button
              className={clsx(isSmallscreenAbove && '-ml-2.5', 'mr-1')}
              size="small"
              disabled={selectedGridCount >= selectableImagesCount}
              onClick={() => selectAll()}
            >
              SELECT ALL
            </Button>

            <Button size="small" disabled={selectedGridCount === 0} onClick={() => deselectAll()}>
              DESELECT ALL
            </Button>
          </div>

          <div className="flex items-center">
            {isSelectedImageEnough ? (
              <Typography
                className="text-white-med-on-dark"
                variant="body2"
              >{`${selectedGridCount} / ${gridCount} images will be in your collection`}</Typography>
            ) : (
              <Typography
                className="text-danger"
                variant="body2"
              >{`Select at least ${values.MINIMUM_SELL_NFT_IMAGE} images`}</Typography>
            )}

            <div className="ml-3">
              <Button
                className="mr-1"
                size="small"
                onClick={() => {
                  cancel()
                  setStep(STEP.SELECT_COVER)
                }}
              >
                CANCEL
              </Button>

              <Button
                className="-mr-2.5"
                color="secondary"
                disabled={!isSelectedImageEnough}
                size="small"
                onClick={() => {
                  apply()
                  setStep(STEP.SELECT_COVER)
                }}
              >
                DONE
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="h-1/100 min-h-0 flex-auto">
        <FixedSizeList<UserImage>
          dataLength={gridImages.length}
          loading={Boolean(gridLoading)}
          config={GRID_PROJECTS_CONFIG}
          renderItem={(props: RenderItemProps<UserImage>) => {
            const { height, index, width } = props

            if (!gridImages[index]) return null

            const item = gridImages[index]

            const selected = SelectUtils.isSelected(
              item.id,
              imageSelectionData.selected,
              imageSelectionData.direction
            )

            return (
              <div className="relative h-full w-full select-none">
                <ButtonBase
                  disabled={item.is_published}
                  className="h-full w-full"
                  onClick={() => toggleSelect(item.id)}
                >
                  <GridImage
                    component="span"
                    containerWidth={width}
                    containerHeight={height}
                    selected={selected}
                    image={item}
                  />
                </ButtonBase>

                <div
                  className={clsx(
                    'pointer-events-none absolute left-2 top-2 z-1 hidden',
                    selected ? 'flex' : null
                  )}
                >
                  <CheckCircleIcon color="secondary" />
                </div>
              </div>
            )
          }}
        />
      </div>
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageSelection)
